import React, { useEffect, useState, useCallback, useRef } from "react";
import _ from "lodash";

import StepWrapper from "./StepWrapper";
import { Col, Form, InputGroup, Row } from "react-bootstrap";
import RenderAmount from "../../../../Components/etc/RenderAmount";
import api from "../../../../Services/Api/api";
import { round2 } from "../../../../Modules/utils/etc";
import { getFullName } from "../../../../Modules/utils/fullname";
import { OwnerCollectionTrList } from "./OwnerCollectionTrList";

const denominations = [2000, 500, 200, 100, 50, 20, 10, 1];

const AmountDetailsStep = ({
  data,
  value,
  onChange,
  onSubmit,
  totalSteps,
  cashLabel = "Cash Amount",
  onlineLabel = "Online Amount",
  adjustmentLabel = "Adjustment",
  totalAmountLabel = "Total Amount",
  adjustmentVisible = true,
  header = null,
  disabled = false,
}) => {
  const useDenomination = !!value.cashDenominations;

  // ---------- HANDLE DENOMINATIONS ----------
  const setDenomination = (denomination, count) => {
    const updated = {
      ...value.cashDenominations,
      [denomination]: parseFloat(count || 0),
    };

    const cashSum = denominations.reduce((sum, d) => {
      return sum + d * (parseFloat(updated[d]) || 0);
    }, 0);

    onChange({
      cashDenominations: updated,
      cashAmount: cashSum,
    });
  };

  // ---------- TOTAL CALCULATION ----------
  const total =
    (parseFloat(value.cashAmount) || 0) +
    (parseFloat(value.onlineAmount) || 0) +
    (adjustmentVisible ? parseFloat(value.adjustedAmount || 0) : 0);

  const disableNext = disabled || total < 0 || isNaN(total);

  return (
    <StepWrapper
      step={data.step}
      total={totalSteps}
      disableNext={disableNext}
      onPrev={() => onSubmit({ step: data.step - 1 })}
      onNext={() => onSubmit({ step: data.step + 1 })}
    >
      {header}

      {/* ---------- CASH AMOUNT ---------- */}
      <Form.Group>
        <Form.Label>{cashLabel}</Form.Label>
        <Form.Control
          type="number"
          placeholder="0"
          value={value.cashAmount || ""}
          onChange={(e) =>
            onChange({ cashAmount: e.target.value, cashDenominations: null })
          }
          disabled={useDenomination}
          min={0}
        />
      </Form.Group>

      {/* Use Denominations Switch */}
      <Form.Group className="mb-3 mt-2">
        <Form.Check
          type="switch"
          label="Use denominations"
          checked={useDenomination}
          onChange={() =>
            onChange(
              useDenomination
                ? { cashDenominations: null }
                : { cashDenominations: {} }
            )
          }
        />
      </Form.Group>

      {/* Denomination List */}
      {useDenomination &&
        denominations.map((d) => (
          <Form.Group
            as={Row}
            className=""
            style={{ marginBottom: "2px" }}
            key={d}
          >
            <Form.Label column xs="4" className="pt-0 pb-0">
              {d === 1 ? "Manual (1x)" : `₹${d} x`}
            </Form.Label>
            <Col xs="8">
              <InputGroup>
                <Form.Control
                  className="pt-0 pb-0"
                  type="number"
                  min={0}
                  placeholder="0"
                  value={value.cashDenominations?.[d] || ""}
                  onChange={(e) => setDenomination(d, e.target.value)}
                />
                <InputGroup.Text
                  className=""
                  style={{ paddingTop: "2px", paddingBottom: "2px" }}
                >
                  = ₹{(value.cashDenominations?.[d] || 0) * d}
                </InputGroup.Text>
              </InputGroup>
            </Col>
          </Form.Group>
        ))}

      {/* ---------- ONLINE AMOUNT ---------- */}
      <Form.Group className="mt-3">
        <Form.Label>{onlineLabel}</Form.Label>
        <Form.Control
          type="number"
          placeholder="0"
          value={value.onlineAmount || ""}
          onChange={(e) => onChange({ onlineAmount: e.target.value })}
          min={0}
          step={0.01}
        />
      </Form.Group>

      {/* ---------- ADJUSTMENT (Optional) ---------- */}
      {adjustmentVisible || value.adjustedAmount ? (
        <Form.Group className="mt-3">
          <Form.Label>{adjustmentLabel}</Form.Label>
          <Form.Control
            type="number"
            value={value.adjustedAmount || 0}
            disabled
            // plaintext
            // className="fw-bold text-primary"
          />
        </Form.Group>
      ) : null}

      {/* ---------- TOTAL DISPLAY ---------- */}
      <Form.Group className="mt-4">
        <Form.Label className="fw-bold">{totalAmountLabel}</Form.Label>
        <div className="p-2 border rounded bg-light fw-bold">
          <RenderAmount value={total} />
        </div>
      </Form.Group>
    </StepWrapper>
  );
};

export const PaidToAgentStep = (props) => {
  const { officeBalance: initialOfficeBalance, isEditing } = props;

  const [officeBalance, setOfficeBalance] = useState(
    initialOfficeBalance ?? null
  );
  const [userTotalDue, setUserTotalDue] = useState(null); // number or null
  const [userTotalDueSnapshotAt, setUserTotalDueSnapshotAt] = useState(null); // timestamp
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // mounted guard
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Normalizer for officeBalance snapshot
  const normalize = useCallback((ob) => {
    if (!ob) {
      return {
        cashAvailable: 0,
        onlineAvailable: 0,
        totalAvailable: 0,
        cashDenominations: null,
        lastUpdatedAt: null,
        lastUpdatedBy: null,
      };
    }
    return {
      cashAvailable: Number(ob.cashAvailable ?? 0),
      onlineAvailable: Number(ob.onlineAvailable ?? 0),
      totalAvailable: Number(ob.totalAvailable ?? 0),
      cashDenominations: ob.cashDenominations ?? null,
      lastUpdatedAt: ob.lastUpdatedAt ?? null,
      lastUpdatedBy: ob.lastUpdatedBy ?? null,
    };
  }, []);

  const fetchBalance = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const tLogP = api.get("v1/office/transaction-log", {
        queryStr: JSON.stringify({ limit: 1, order: -1, sortby: "timestamp" }),
      });

      const selectedUserId = props.data?.agent?._id;
      const { transactions } = selectedUserId
        ? await api.get("v1/office/transaction", {
            queryStr: JSON.stringify({
              limit: 1,
              order: -1,
              where: {
                relatedUser: selectedUserId,
              },
            }),
          })
        : {};

      const lastTr = transactions?.[0];
      if (lastTr && lastTr?.relatedUser !== selectedUserId) {
        throw new Error("Error fetching user balance");
      }
      setUserTotalDue(lastTr?.userBalanceDue?.total);
      setUserTotalDueSnapshotAt(lastTr?.date);

      const { transactionLogs } = await tLogP;
      const balance = transactionLogs?.[0]?.balanceAfter;

      console.log({
        balance,
        transactionLogs,
        transactions,
        lastTr,
        selectedUserId,
      });
      setOfficeBalance(balance || null);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [props]);

  useEffect(() => {
    fetchBalance();
  }, []);

  // Autofill logic: when not editing, when onSubmit exists, when office snapshot + server total due available,
  // fill cashAmount and onlineAmount if they are empty. Prefers cash first until cash balance exhausted,
  // then use online. Treat negative totalDue as payble amount.
  useEffect(() => {
    // only autofill when not editing and parent gave onSubmit
    if (isEditing) return;
    if (typeof props.onSubmit !== "function") return;
    if (!officeBalance && userTotalDue === null) return; // need at least snapshot or server total

    const ob = normalize(officeBalance);

    // prefer userTotalDue if provided, otherwise fallback to totalAvailable from snapshot
    // negative userTotalDue means, office need to pay.
    let due = Number.isFinite(userTotalDue) ? -userTotalDue : 0;

    // If due is NaN or not finite, treat as 0
    if (!Number.isFinite(due)) due = 0;

    // Do not autofill with negative due — set to 0
    if (due < 0) due = 0;

    const existing = props.data?.totalAmountDetails ?? {};

    const cashEmpty =
      existing.cashAmount === undefined ||
      existing.cashAmount === null ||
      existing.cashAmount === "";
    const onlineEmpty =
      existing.onlineAmount === undefined ||
      existing.onlineAmount === null ||
      existing.onlineAmount === "";

    // If both already filled, nothing to do
    if (!cashEmpty && !onlineEmpty) return;

    // Determine how much to take from cash first, then online
    // Use available cash / online from snapshot
    const availableCash = Math.max(0, ob.cashAvailable ?? 0);
    const availableOnline = Math.max(0, ob.onlineAvailable ?? 0);

    // Start with remainingDue = due
    let remainingDue = Math.max(0, due);

    // If cash field is empty, fill from cashAvailable up to remainingDue
    let fillCash =
      typeof existing.cashAmount === "string"
        ? existing.cashAmount
        : existing.cashAmount;
    if (cashEmpty) {
      fillCash = Math.min(availableCash, remainingDue);
      remainingDue = remainingDue - fillCash;
    } else {
      // keep existing (do not overwrite)
      fillCash = existing.cashAmount;
    }

    // If online field is empty, fill from availableOnline up to remainingDue
    let fillOnline =
      typeof existing.onlineAmount === "string"
        ? existing.onlineAmount
        : existing.onlineAmount;
    if (onlineEmpty) {
      fillOnline = Math.min(availableOnline, Math.max(0, remainingDue));
      remainingDue = remainingDue - fillOnline;
    } else {
      fillOnline = existing.onlineAmount;
    }

    // Ensure strings because inputs expect strings
    const filled = {
      ...(existing || {}),
      cashAmount: cashEmpty
        ? Math.round(Number(fillCash) || 0)
        : existing.cashAmount,
      onlineAmount: onlineEmpty
        ? round2(Number(fillOnline) || 0)
        : existing.onlineAmount,
    };

    const dataToSubmit = {
      ...(props.data || {}),
      totalAmountDetails: filled,
    };

    const total =
      (parseFloat(filled.cashAmount) || 0) +
      (parseFloat(filled.onlineAmount) || 0) +
      (parseFloat(filled.adjustedAmount) || 0);

    // call onSubmit to update parent state with autofill
    props.onSubmit({
      ...dataToSubmit,
      totalAmount: total,
    });
  }, [
    officeBalance,
    userTotalDue,
    userTotalDueSnapshotAt,
    isEditing,
    props.onSubmit,
    props.data,
    normalize,
  ]);

  // onChange handler same pattern as original code
  const handleChange = (x) => {
    const currentData = {
      ...(props.data || {}),
      totalAmountDetails: {
        ...(props.data?.totalAmountDetails || {}),
        ...x,
      },
    };

    const total =
      (parseFloat(currentData?.totalAmountDetails?.cashAmount) || 0) +
      (parseFloat(currentData?.totalAmountDetails?.onlineAmount) || 0) +
      (parseFloat(currentData?.totalAmountDetails?.adjustedAmount) || 0);

    if (typeof props.onSubmit === "function") {
      props.onSubmit({
        ...currentData,
        totalAmount: total,
      });
    }
  };

  const ob = normalize(officeBalance);

  // Display: available balances, total balance due, snapshot date for total balance due
  const displayTotalDue = Number.isFinite(userTotalDue) ? userTotalDue : null;
  const snapshotAt = userTotalDueSnapshotAt ?? ob.lastUpdatedAt ?? null;

  return (
    <>
      <div className="mb-3 p-2 border rounded bg-light d-flex align-items-center justify-content-between">
        <div>
          <div className="fw-bold">Available balances</div>

          {loading ? (
            <div>Loading balances…</div>
          ) : error ? (
            <div className="text-danger">Error: {error}</div>
          ) : (
            <>
              <div>
                Cash available: ₹{Number(ob.cashAvailable).toLocaleString()}
              </div>
              <div>
                Online available: ₹{Number(ob.onlineAvailable).toLocaleString()}
              </div>

              <div className="mt-2">
                <div className="small text-muted">
                  Total balance due for FE:
                </div>
                <div>
                  <strong>
                    ₹
                    {displayTotalDue !== null
                      ? Number(displayTotalDue).toLocaleString()
                      : Number(ob.totalAvailable).toLocaleString()}
                  </strong>
                </div>

                <div className="small text-muted">
                  Snapshot:{" "}
                  {snapshotAt ? new Date(snapshotAt).toLocaleString() : "—"}
                </div>
              </div>
            </>
          )}
        </div>

        <div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={fetchBalance}
            disabled={loading}
            title="Refresh available balances and total due"
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <AmountDetailsStep
        {...{
          ...props,
          cashLabel: "Cash Payment to FE",
          onlineLabel: "Online Payment to FE",
          adjustmentLabel: "Paid RTO Loss",
          totalAmountLabel: "Total Amount Paid to FE",
          value: props.data?.totalAmountDetails || {},
          onChange: (x) => handleChange(x),
          // keep adjustment visibility same as before; set to false if you hide it
          adjustmentVisible: false,
        }}
      />
    </>
  );
};

export const EmployeeSalaryPayoutStep = (props) => {
  const [prevBalanceDue, setPrevBalanceDue] = useState(null);
  const [snapshotAt, setSnapshotAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch previous balance due on mount
  useEffect(() => {
    const fetchBalanceDue = async () => {
      setLoading(true);
      setError(null);

      try {
        const employeeId = props.data?.agent?._id;
        if (!employeeId) {
          throw new Error("Employee not selected");
        }

        const { transactions } = await api.get("v1/office/transaction", {
          queryStr: JSON.stringify({
            limit: 1,
            order: -1,
            sortby: "date",
            where: { relatedUser: employeeId },
          }),
        });

        const lastTr = transactions?.[0];
        setPrevBalanceDue(Number(lastTr?.userBalanceDue?.total || 0));
        setSnapshotAt(lastTr?.date || null);
      } catch (e) {
        setError(e?.message || "Failed to load employee balance");
      } finally {
        setLoading(false);
      }
    };

    fetchBalanceDue();
  }, []);

  // Derived values (ledger model)
  const salaryAmount = Number(props.data?.salaryDetails?.baseSalary || 0);

  const cash = parseFloat(props.data?.totalAmountDetails?.cashAmount);
  const online = parseFloat(props.data?.totalAmountDetails?.onlineAmount);

  const totalPaid = (cash || 0) + (online || 0);

  const netPayable =
    prevBalanceDue !== null ? Math.max(0, salaryAmount - prevBalanceDue) : 0;

  const remainingForOnline =
    prevBalanceDue !== null
      ? Math.max(0, netPayable - (parseFloat(cash) || 0))
      : 0;

  const balanceDueAfterSalary =
    prevBalanceDue !== null ? prevBalanceDue - salaryAmount + totalPaid : null;

  // Disable all inputs until balance is loaded
  const inputsDisabled = loading || !!error || prevBalanceDue === null;

  const handleChange = (x) => {
    const totalAmountDetails = {
      ...(props.data?.totalAmountDetails || {}),
      ...x,
    };

    const total =
      (parseFloat(totalAmountDetails.cashAmount) || 0) +
      (parseFloat(totalAmountDetails.onlineAmount) || 0);

    props.onSubmit({
      ...(props.data || {}),
      totalAmountDetails,
      totalAmount: total,
    });
  };

  return (
    <>
      {/* -------- Balance Status -------- */}
      <div className="mb-3 p-2 border rounded bg-light">
        {loading ? (
          <div>Loading employee balance…</div>
        ) : error ? (
          <div className="text-danger">{error}. Inputs are disabled.</div>
        ) : (
          <>
            <div>
              <strong>Previous balance due:</strong> ₹
              {prevBalanceDue.toLocaleString()}
            </div>

            <div className="small text-muted">
              Snapshot:{" "}
              {snapshotAt ? new Date(snapshotAt).toLocaleString() : "—"}
            </div>

            <hr className="my-2" />

            <div>
              <strong>Net payable (salary − due):</strong> ₹
              {netPayable.toLocaleString()}
            </div>

            <div className="small text-muted">
              Employee Salary Amount: ₹{salaryAmount.toLocaleString()}
            </div>
            <div className="small text-muted">
              Balance due after payout: ₹
              {balanceDueAfterSalary.toLocaleString()}
            </div>
          </>
        )}
      </div>

      {/* -------- Cash / Online Inputs -------- */}
      <AmountDetailsStep
        {...props}
        cashLabel="Cash Payment to Employee"
        onlineLabel={`Online Payment to Employee${
          !inputsDisabled
            ? ` (Remaining ₹${remainingForOnline.toLocaleString()})`
            : ""
        }`}
        totalAmountLabel="Total Amount Paid"
        value={props.data?.totalAmountDetails || {}}
        onChange={handleChange}
        adjustmentVisible={false}
        disabled={inputsDisabled}
        requireCash
        requireOnline
      />
    </>
  );
};

export const PaidInByAgentStep = (props) => {
  const [prevBalanceDue, setPrevBalanceDue] = useState(null);
  const [snapshotAt, setSnapshotAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBalanceDue = async () => {
      setLoading(true);
      setError(null);

      try {
        const employeeId = props.data?.agent?._id;
        if (!employeeId) {
          throw new Error("FE not selected");
        }

        const { transactions } = await api.get("v1/office/transaction", {
          queryStr: JSON.stringify({
            limit: 1,
            order: -1,
            sortby: "date",
            where: { relatedUser: employeeId },
          }),
        });

        const lastTr = transactions?.[0];
        setPrevBalanceDue(Number(lastTr?.userBalanceDue?.total || 0));
        setSnapshotAt(lastTr?.date || null);
      } catch (e) {
        setError(e?.message || "Failed to load employee balance");
      } finally {
        setLoading(false);
      }
    };

    fetchBalanceDue();
  }, []);

  const cash = parseFloat(props.data?.totalAmountDetails?.cashAmount);
  const online = parseFloat(props.data?.totalAmountDetails?.onlineAmount);
  const totalPaid = (cash || 0) + (online || 0);

  const balanceDueAfter = (prevBalanceDue || 0) - totalPaid;

  const inputsDisabled = loading || !!error;

  return (
    <>
      <div className="mb-3 p-2 border rounded bg-light">
        {loading ? (
          <div>Loading balance…</div>
        ) : error ? (
          <div className="text-danger">{error}. Inputs are disabled.</div>
        ) : (
          <>
            <div>
              <strong>Previous balance due:</strong> ₹
              {prevBalanceDue.toLocaleString()}
            </div>

            <div className="small text-muted">
              Snapshot:{" "}
              {snapshotAt ? new Date(snapshotAt).toLocaleString() : "—"}
            </div>

            <hr className="my-2" />

            <div className="small text-muted">
              Balance due after payment: ₹{balanceDueAfter.toLocaleString()}
            </div>
          </>
        )}
      </div>
      <AmountDetailsStep
        {...{
          ...props,
          cashLabel: "Cash Deposited By FE",
          onlineLabel: "Online Deposited By FE",
          totalAmountLabel: "Total Amount Deposited By FE",
          disabled: inputsDisabled,

          value: props.data?.totalAmountDetails || {},
          onChange: (x) => {
            const data = {
              ...(props.data || {}),
              totalAmountDetails: {
                ...(props.data.totalAmountDetails || {}),
                ...x,
              },
            };
            const total =
              (parseFloat(data?.totalAmountDetails?.cashAmount) || 0) +
              (parseFloat(data?.totalAmountDetails?.onlineAmount) || 0);

            props.onSubmit({
              ...data,
              totalAmount: total,
            });
          },

          adjustmentVisible: false,
        }}
      />
    </>
  );
};

export const CodPaidInByAgentStep = (props) => {
  const [prevBalanceDue, setPrevBalanceDue] = useState(null);
  const [prevComBal, setPrevComBal] = useState(null);
  const [snapshotAt, setSnapshotAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const employeeId = props.data?.agent?._id;
        if (!employeeId) {
          throw new Error("FE not selected");
        }

        const { transactions } = await api.get("v1/office/transaction", {
          queryStr: JSON.stringify({
            limit: 1,
            order: -1,
            sortby: "date",
            where: { relatedUser: employeeId },
          }),
        });

        const lastTr = transactions?.[0];
        setPrevBalanceDue(Number(lastTr?.userBalanceDue?.total || 0));
        setPrevComBal(Number(lastTr?.commissionBalance?.total || 0));
        setSnapshotAt(lastTr?.date || null);
      } catch (e) {
        setError(e?.message || "Failed to load employee balance");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const { isEditing, data: d } = props;
  const cash = parseFloat(props.data?.totalAmountDetails?.cashAmount);
  const online = parseFloat(props.data?.totalAmountDetails?.onlineAmount);
  const totalPaid = (cash || 0) + (online || 0);
  const totalCOD = Number(props.data.cod?.totalCOD || 0);

  const balanceDueAfter = totalCOD + (prevBalanceDue || 0) - totalPaid;

  const inputsDisabled = loading || !!error;

  // commission
  const deliveryCount = Number(d.cod.deliveryCount || 0);
  const pickupCount = Number(d.cod.pickupCount || 0);
  const deliveryRate = Number(d.cod.deliveryRate || 0);
  const pickupRate = Number(d.cod.pickupRate || 0);

  const currentCommission =
    deliveryCount * deliveryRate + pickupCount * pickupRate;

  return (
    <>
      <div className="mb-3 p-2 border rounded bg-light">
        <div className="small ">
          <strong>COD Amount</strong> ₹
          {props.data.cod?.totalCOD?.toLocaleString()}
        </div>
        <div className="small ">
          <strong>Current Commission:</strong> ₹{currentCommission}
        </div>
        <div className="small ">
          <strong>Current Balance Due: </strong>₹
          {props.data.cod?.totalCOD - props.data.totalAmount}
        </div>

        <hr className="my-2" />

        {loading ? (
          <div className="small ">Loading Balances</div>
        ) : error ? (
          <div className="text-danger">{error}</div>
        ) : (
          <>
            <div className="small ">
              <strong>Commission Balance:</strong> ₹
              {prevComBal.toLocaleString()}
            </div>
            <div className="small ">
              <strong>User Balance Due:</strong> ₹
              {prevBalanceDue.toLocaleString()}
            </div>

            <div className="small text-muted">
              Snapshot:{" "}
              {snapshotAt ? new Date(snapshotAt).toLocaleString() : "—"}
            </div>

            <hr className="my-2" />

            {!isEditing && (
              <div className="small ">
                Balance due after payment: ₹{balanceDueAfter.toLocaleString()}
              </div>
            )}
          </>
        )}
      </div>

      <AmountDetailsStep
        {...{
          ...props,
          cashLabel: "Cash Deposited By FE",
          onlineLabel: (
            <span>
              Online Deposited By FE (Total COD - Cash = ₹
              <span className="fw-semibold">
                {Number(props.data.cod?.totalCOD || 0) -
                  (props.data?.totalAmountDetails?.cashAmount || 0)}
                )
              </span>
            </span>
          ),
          totalAmountLabel: "Total Amount Deposited By FE",

          value: props.data?.totalAmountDetails || {},
          onChange: (x) => {
            const data = {
              ...(props.data || {}),
              totalAmountDetails: {
                ...(props.data.totalAmountDetails || {}),
                ...x,
              },
            };
            const total =
              (parseFloat(data?.totalAmountDetails?.cashAmount) || 0) +
              (parseFloat(data?.totalAmountDetails?.onlineAmount) || 0);

            props.onSubmit({
              ...data,
              totalAmount: total,
            });
          },

          adjustmentVisible: false,
        }}
      />
    </>
  );
};

export const OfficeExpenseAmountStep = (props) => (
  <AmountDetailsStep
    {...{
      ...props,
      cashLabel: "Cash Amount",
      onlineLabel: "Online Amount",
      totalAmountLabel: "Total Expense Amount",
      value: props.data?.totalAmountDetails || {},
      onChange: (x) => {
        const data = {
          ...(props.data || {}),
          totalAmountDetails: {
            ...(props.data.totalAmountDetails || {}),
            ...x,
          },
        };
        const total =
          (parseFloat(data?.totalAmountDetails?.cashAmount) || 0) +
          (parseFloat(data?.totalAmountDetails?.onlineAmount) || 0) +
          (parseFloat(data?.totalAmountDetails?.adjustedAmount) || 0);

        props.onSubmit({
          ...data,
          totalAmount: total,
        });
      },

      adjustmentVisible: false,
    }}
  />
);

export const OwnerCollectionAmountStep = (props) => {
  const { officeBalance: initialOfficeBalance, isEditing } = props;

  const [officeBalance, setOfficeBalance] = useState(
    initialOfficeBalance || null
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper: standardized numbers from the officeBalance snapshot (safe defaults)
  const normalize = useCallback((ob) => {
    if (!ob)
      return {
        cashAvailable: 0,
        onlineAvailable: 0,
        totalAvailable: 0,
        cashDenominations: null,
      };
    return {
      cashAvailable: Number(
        ob.cashAvailable || (ob.cashAvailable === 0 ? 0 : 0)
      ),
      onlineAvailable: Number(
        ob.onlineAvailable || (ob.onlineAvailable === 0 ? 0 : 0)
      ),
      totalAvailable: Number(
        ob.totalAvailable || (ob.totalAvailable === 0 ? 0 : 0)
      ),
      cashDenominations: ob.cashDenominations || null,
      lastUpdatedAt: ob.lastUpdatedAt || null,
      lastUpdatedBy: ob.lastUpdatedBy || null,
    };
  }, []);

  // Fetch function: prefer props.fetchOfficeBalance, otherwise default to api.get(...)
  const fetchBalance = useCallback(
    async (_id) => {
      setLoading(true);
      setError(null);

      try {
        const { transactionLogs } = await api.get("v1/office/transaction-log", {
          queryStr: JSON.stringify({
            limit: 1,
            order: -1,
            sortby: "timestamp",
          }),
        });
        const balance = transactionLogs?.[0]?.balanceAfter;

        setOfficeBalance(balance || null);
      } catch (err) {
        setError(err?.message || String(err));
      } finally {
        setLoading(false);
      }
    },
    [props]
  );

  // On mount: if parent passed initialOfficeBalance, use it immediately, then fetch updated data.
  useEffect(() => {
    if (initialOfficeBalance) {
      setOfficeBalance(initialOfficeBalance);
    }
    // always try to refresh from server to get latest snapshot
    fetchBalance(props.data?._id);
  }, [props.data?._id]); // run once on mount

  // Autofill when not editing and cash/online fields are empty
  useEffect(() => {
    // do nothing if editing or no parent onSubmit provided
    if (isEditing) return;
    if (!props.onSubmit) return;
    if (!officeBalance) return;

    const ob = normalize(officeBalance);
    const existing = props.data?.totalAmountDetails || {};

    const cashEmpty =
      existing.cashAmount === undefined ||
      existing.cashAmount === null ||
      existing.cashAmount === "";
    const onlineEmpty =
      existing.onlineAmount === undefined ||
      existing.onlineAmount === null ||
      existing.onlineAmount === "";

    console.log({ cashEmpty, onlineEmpty, ob, existing });
    // if neither empty, nothing to autofill
    if (!cashEmpty && !onlineEmpty) return;

    // prepare filled object (strings kept because inputs expect strings)
    const filled = {
      ...(existing || {}),
      cashAmount: cashEmpty ? String(ob.cashAvailable) : existing.cashAmount,
      onlineAmount: onlineEmpty
        ? String(ob.onlineAvailable)
        : existing.onlineAmount,
    };

    const dataToSubmit = {
      ...(props.data || {}),
      totalAmountDetails: filled,
    };

    const total =
      (parseFloat(filled.cashAmount) || 0) +
      (parseFloat(filled.onlineAmount) || 0) +
      (parseFloat(filled.adjustedAmount) || 0);

    // call onSubmit immediately to update parent state with autofill
    props.onSubmit({
      ...dataToSubmit,
      totalAmount: total,
    });
    // run whenever officeBalance changes (fresh server data) or data changes
  }, [officeBalance, isEditing]);

  // Use same change handler pattern as original component
  const handleChange = (x) => {
    const currentData = {
      ...(props.data || {}),
      totalAmountDetails: {
        ...(props.data?.totalAmountDetails || {}),
        ...x,
      },
    };

    const total =
      (parseFloat(currentData?.totalAmountDetails?.cashAmount) || 0) +
      (parseFloat(currentData?.totalAmountDetails?.onlineAmount) || 0) +
      (parseFloat(currentData?.totalAmountDetails?.adjustedAmount) || 0);

    props.onSubmit({
      ...currentData,
      totalAmount: total,
    });
  };

  const ob = normalize(officeBalance);

  const remainingCash =
    Number(ob?.cashAvailable || 0) -
    Number(props.data?.totalAmountDetails?.cashAmount || 0);

  const INR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
  const fmtDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <>
      <OwnerCollectionTrList
        key={props.data?._id}
        transactionId={props.data?._id}
        onSummary={(x) =>
          x &&
          props.onSubmit({
            meta: {
              ...(props.data?.meta || {}),
              ..._.pick(x, [
                "range",
                "overall",
                "officeExp",
                "firstTr",
                "lastTr",
              ]),
            },
          })
        }
      />

      <div className="mb-3 p-2 border rounded bg-light">
        <div>
          <div className="d-flex align-items-center justify-content-between">
            <div className="fw-bold">Current office balance</div>
            <div>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => fetchBalance(props.data._id)}
                disabled={loading}
                title="Refresh available balances"
              >
                {loading ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>

          {loading ? (
            <div>Loading balances…</div>
          ) : error ? (
            <div className="text-danger">Error: {error}</div>
          ) : (
            <div>
              <Row sm={3}>
                <Col>Cash: ₹{Number(ob.cashAvailable).toLocaleString()}</Col>
                <Col>
                  Online: ₹{Number(ob.onlineAvailable).toLocaleString()}
                </Col>
                <Col>Total: ₹{Number(ob.totalAvailable).toLocaleString()}</Col>
              </Row>
              <div className="small text-muted">
                Snapshot:{" "}
                {ob.lastUpdatedAt
                  ? new Date(ob.lastUpdatedAt).toLocaleString()
                  : "—"}
              </div>
            </div>
          )}
        </div>
      </div>

      {props.data.officeBalance?.before ? (
        <div className="mb-3 p-2 border rounded bg-light">
          <div>
            <div className="fw-bold">
              Available amount before this transaction
            </div>

            <div>
              <Row sm={3}>
                <Col>
                  Cash: ₹
                  {Number(
                    props.data.officeBalance?.before.cashAvailable
                  ).toLocaleString()}
                </Col>
                <Col>
                  Online: ₹
                  {Number(
                    props.data.officeBalance?.before.onlineAvailable
                  ).toLocaleString()}
                </Col>
                <Col>
                  Total: ₹
                  {Number(
                    props.data.officeBalance?.before.totalAvailable
                  ).toLocaleString()}
                </Col>
              </Row>
              <div className="small text-muted">
                Snapshot:{" "}
                {ob.lastUpdatedAt
                  ? new Date(
                      props.data.officeBalance?.before.lastUpdatedAt
                    ).toLocaleString()
                  : "—"}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <AmountDetailsStep
        {...{
          ...props,
          cashLabel: (
            <span>
              Cash Collected{" "}
              <span className="text-muted">
                (Remaining:{" "}
                <span
                  style={
                    remainingCash !== 0 ? { color: "red" } : { color: "green" }
                  }
                >
                  ₹{remainingCash.toLocaleString()}
                </span>
                )
              </span>
            </span>
          ),
          onlineLabel: "Online Collected",
          totalAmountLabel: "Total Collection Amount",
          value: props.data?.totalAmountDetails || {},
          onChange: (x) => handleChange(x),
          // we keep adjustment hidden for owner collection as before
          adjustmentVisible: false,
        }}
      />
    </>
  );
};

export default AmountDetailsStep;
